import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import * as CIPCService from '@/services/cipcService';

interface CIPCFilingJobsProps {
  companyId: string;
}

export function CIPCFilingJobs({ companyId }: CIPCFilingJobsProps) {
  const [jobs, setJobs] = useState<CIPCService.CIPCFilingJob[]>([]);
  const [loading, setLoading] = useState(true);

  async function loadJobs() {
    try {
      setLoading(true);
      const data = await CIPCService.getCIPCFilingJobs(companyId);
      setJobs(data);
    } catch (err: any) {
      console.error('Error loading CIPC jobs:', err);
      toast.error('Failed to load CIPC filing jobs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (companyId) {
      loadJobs();
    }

    // Subscribe to real-time updates
    const channel = supabase
      .channel('cipc_filing_jobs')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cipc_filing_jobs',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          loadJobs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FILED':
        return 'bg-green-500 text-white';
      case 'GENERATED':
        return 'bg-yellow-500 text-white';
      case 'SUPERSEDED':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'FILED':
        return 'Filed with CIPC';
      case 'GENERATED':
        return 'Awaiting manual filing';
      case 'SUPERSEDED':
        return 'Superseded';
      default:
        return status;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Statutory Filings</CardTitle>
        <Button variant="outline" size="sm" onClick={loadJobs} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : jobs.length > 0 ? (
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Form Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>CIPC Reference</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Document</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-medium">{job.filing_form}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(job.status)}>
                        {getStatusText(job.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{job.submission_ref || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {new Date(job.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(job.filing_url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center p-8 border rounded-lg">
            <p className="text-sm text-muted-foreground">
              No CIPC submissions yet. Generate a filing form and submit it to CIPC.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
